import { computed, type Ref } from 'vue';

export type PathError = {
  hasError: boolean;
};

export type RuleError = {
  hasError: boolean;
  paths: PathError[];
};

type ErrorBaseOptions = {
  clearError?: boolean;
  reverse?: boolean;
};

const getErrors = (
  errorsRaw: unknown[][] | null | undefined,
  errorCondition: unknown[],
  outIndexes?: number[],
) => {
  if (!errorsRaw) return [];

  return errorsRaw.filter((error, index) => {
    const result = errorCondition.every((item, conditionIndex) => item === error[conditionIndex]);
    if (outIndexes && result) {
      outIndexes.push(index);
    }
    return result;
  });
};

const getEmptyEntityErrorBase = (
  errorsRaw: unknown[][] | null | undefined,
  defaultErrorCondition: unknown[],
  condition: unknown[],
  errorCondition: unknown[] | null,
  options: ErrorBaseOptions = {},
) => {
  const clearError = options.clearError || false;
  const reverse = options.reverse || false;

  let errors: unknown[][] | null = null;
  let indexes: number[] | null = clearError ? [] : null;

  const resolvedErrorCondition = errorCondition ?? defaultErrorCondition;

  if (errorCondition) {
    errors = getErrors(errorsRaw, errorCondition, indexes ?? undefined);
  } else {
    errors = getErrors(errorsRaw, defaultErrorCondition);
  }

  const index = resolvedErrorCondition.length;
  const conditionFunc = (error: unknown[]) => {
    for (let errorIndex = index, conditionIndex = 0; conditionIndex < condition.length; errorIndex++, conditionIndex++) {
      if (error[errorIndex] !== condition[conditionIndex]) {
        return reverse ? true : false;
      }
    }
    return reverse ? false : true;
  };

  if (clearError) {
    const result = errors.map(conditionFunc);
    return (errorsRaw || []).map((item, itemIndex) => {
      const matchIndex = indexes?.indexOf(itemIndex) ?? -1;
      return matchIndex !== -1 ? result[matchIndex] : false;
    });
  }

  return errors.some(conditionFunc);
};

const getEmptyEntityError = (
  errorsRaw: unknown[][] | null | undefined,
  defaultErrorCondition: unknown[],
  condition: unknown[],
  errorCondition: unknown[] | null = null,
  reverse = false,
) =>
  getEmptyEntityErrorBase(errorsRaw, defaultErrorCondition, condition, errorCondition, { reverse }) as boolean;

const getInvalidFieldsErrorBase = (
  errorsRaw: unknown[][] | null | undefined,
  defaultErrorCondition: unknown[],
  condition: unknown[],
  errorCondition: unknown[] | null,
  options: ErrorBaseOptions = {},
) => {
  const clearError = options.clearError || false;
  const reverse = options.reverse || false;

  let errors: unknown[][] | null = null;
  let indexes: number[] | null = clearError ? [] : null;

  const resolvedErrorCondition = errorCondition ?? defaultErrorCondition;

  if (errorCondition) {
    errors = getErrors(errorsRaw, errorCondition, indexes ?? undefined);
  } else {
    errors = getErrors(errorsRaw, defaultErrorCondition);
  }

  const index = resolvedErrorCondition.length;
  const conditionFunc = (error: unknown[]) =>
    reverse ? !condition.includes(error[index]) : condition.includes(error[index]);

  if (clearError) {
    const result = errors.map(conditionFunc);
    return (errorsRaw || []).map((item, itemIndex) => {
      const matchIndex = indexes?.indexOf(itemIndex) ?? -1;
      return matchIndex !== -1 ? result[matchIndex] : false;
    });
  }

  return errors.some(conditionFunc);
};

const getInvalidFieldsError = (
  errorsRaw: unknown[][] | null | undefined,
  defaultErrorCondition: unknown[],
  condition: unknown[],
  errorCondition: unknown[] | null = null,
  reverse = false,
) =>
  getInvalidFieldsErrorBase(errorsRaw, defaultErrorCondition, condition, errorCondition, { reverse }) as boolean;

const getEmptyEntityErrors = (
  errorsRaw: unknown[][] | null | undefined,
  condition: unknown[],
  errorCondition: unknown[] | null = null,
  reverse = false,
) =>
  getEmptyEntityErrorBase(errorsRaw, [], condition, errorCondition, {
    clearError: true,
    reverse,
  }) as boolean[];

const getRuleErrorCondition = (ruleIndex: number) => ['rules', ruleIndex + 1];

const getPathErrorCondition = (ruleIndex: number, pathIndex: number) => [
  'rules',
  ruleIndex + 1,
  'paths',
  pathIndex + 1,
];

const getOfferErrorCondition = (ruleIndex: number, pathIndex: number, offerIndex: number) => [
  'rules',
  ruleIndex + 1,
  'paths',
  pathIndex + 1,
  'offers',
  offerIndex + 1,
];

const getLandingErrorCondition = (ruleIndex: number, pathIndex: number, landingIndex: number) => [
  'rules',
  ruleIndex + 1,
  'paths',
  pathIndex + 1,
  'landings',
  landingIndex + 1,
];

const isNoConditionErrorCondition = ['VALIDATION_FLOW_RULE_AT_LEAST_ONE_CONDITION', 'conditions'];
const isAtLeastOneActivePathCondition = ['VALIDATION_FLOW_AT_LEAST_ONE_ACTIVE_PATH', 'paths', 0];
const isNotPathErrorCondition = ['paths'];
const emptyLandingsCondition = ['landings', 1, 'VALIDATION_FLOW_PATH_AT_LEAST_ONE_LANDING'];
const emptyOffersCondition = ['offers', 1, 'VALIDATION_FLOW_PATH_AT_LEAST_ONE_OFFER'];
const pathWeightErrorCondition = ['weight'];
const offerGeneralErrorCondition = ['id'];
const offerWeightErrorCondition = ['weight'];
const landingGeneralErrorCondition = ['id'];
const landingWeightErrorCondition = ['weight'];

export const useFlowNavigationErrors = (
  errorsRaw: Ref<unknown[][] | null | undefined>,
  errorConditionPrefix: Ref<string[]>,
) => {
  const isPathError = (
    ruleIndex: number,
    path: { offers: unknown[]; landings: unknown[] },
    pathIndex: number,
  ) => {
    const pathCondition = errorConditionPrefix.value.concat(getPathErrorCondition(ruleIndex, pathIndex));

    if (
      getEmptyEntityError(errorsRaw.value, [], emptyLandingsCondition, pathCondition)
      || getEmptyEntityError(errorsRaw.value, [], emptyOffersCondition, pathCondition)
      || getInvalidFieldsError(errorsRaw.value, [], pathWeightErrorCondition, pathCondition)
    ) {
      return true;
    }

    const { offers, landings } = path;

    for (let offerIndex = 0; offerIndex < offers.length; offerIndex++) {
      const offerCondition = errorConditionPrefix.value.concat(
        getOfferErrorCondition(ruleIndex, pathIndex, offerIndex),
      );

      if (
        getInvalidFieldsError(errorsRaw.value, [], offerGeneralErrorCondition, offerCondition)
        || getInvalidFieldsError(errorsRaw.value, [], offerWeightErrorCondition, offerCondition)
      ) {
        return true;
      }
    }

    for (let landingIndex = 0; landingIndex < landings.length; landingIndex++) {
      const landingCondition = errorConditionPrefix.value.concat(
        getLandingErrorCondition(ruleIndex, pathIndex, landingIndex),
      );

      if (
        getInvalidFieldsError(errorsRaw.value, [], landingGeneralErrorCondition, landingCondition)
        || getInvalidFieldsError(errorsRaw.value, [], landingWeightErrorCondition, landingCondition)
      ) {
        return true;
      }
    }

    return false;
  };

  const getRulesErrors = (rules: Array<{ paths: Array<{ offers: unknown[]; landings: unknown[] }> }>) => {
    const result: RuleError[] = [];

    for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
      const ruleCondition = errorConditionPrefix.value.concat(getRuleErrorCondition(ruleIndex));
      const rule = rules[ruleIndex];

      const ruleResult: RuleError = {
        hasError:
          getInvalidFieldsError(errorsRaw.value, [], isNoConditionErrorCondition, ruleCondition)
          || getInvalidFieldsError(errorsRaw.value, [], isAtLeastOneActivePathCondition, ruleCondition)
          || getInvalidFieldsError(errorsRaw.value, [], isNotPathErrorCondition, ruleCondition, true),
        paths: [],
      };

      for (let pathIndex = 0; pathIndex < rule.paths.length; pathIndex++) {
        const path = rule.paths[pathIndex];
        ruleResult.paths.push({
          hasError: isPathError(ruleIndex, path, pathIndex),
        });
      }

      result.push(ruleResult);
    }

    return result;
  };

  const isGeneralError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, [], ['rules'], [], true),
  );

  const offset = computed(() => errorConditionPrefix.value.length);

  const mergeErrors = (accumulator: boolean[], next: boolean[]) =>
    accumulator.map((item, index) => item || next[index] || false);

  const processRuleError = (ruleIndex: number) => {
    const ruleCondition = errorConditionPrefix.value.concat(getRuleErrorCondition(ruleIndex));
    const errorFlags: boolean[][] = [getEmptyEntityErrors(errorsRaw.value, [], ruleCondition)];
    return errorFlags.reduce(mergeErrors, (errorsRaw.value || []).map(() => false));
  };

  const processPathError = (ruleIndex: number, pathIndex: number) => {
    const pathCondition = errorConditionPrefix.value.concat(
      getPathErrorCondition(ruleIndex, pathIndex),
    );
    const errorFlags: boolean[][] = [getEmptyEntityErrors(errorsRaw.value, [], pathCondition)];
    return errorFlags.reduce(mergeErrors, (errorsRaw.value || []).map(() => false));
  };

  const processOfferError = (ruleIndex: number, pathIndex: number, offerIndex: number) => {
    const offerCondition = errorConditionPrefix.value.concat(
      getOfferErrorCondition(ruleIndex, pathIndex, offerIndex),
    );
    const errorFlags: boolean[][] = [getEmptyEntityErrors(errorsRaw.value, [], offerCondition)];
    return errorFlags.reduce(mergeErrors, (errorsRaw.value || []).map(() => false));
  };

  const processLandingError = (ruleIndex: number, pathIndex: number, landingIndex: number) => {
    const landingCondition = errorConditionPrefix.value.concat(
      getLandingErrorCondition(ruleIndex, pathIndex, landingIndex),
    );
    const errorFlags: boolean[][] = [getEmptyEntityErrors(errorsRaw.value, [], landingCondition)];
    return errorFlags.reduce(mergeErrors, (errorsRaw.value || []).map(() => false));
  };

  const fixPathErrorIndexes = (
    ruleIndex: number,
    pathIndex: number,
    pathErrors: boolean[],
  ) => {
    const ruleErrors = processRuleError(ruleIndex);
    return (errorsRaw.value || [])
      .map((item, index) => {
        if (!ruleErrors[index]) {
          return item;
        }

        const errorItem = item as unknown[];
        if (Number(errorItem[offset.value + 3]) > pathIndex + 1) {
          const updated = errorItem.slice(0);
          updated[offset.value + 3] = Number(updated[offset.value + 3]) - 1;
          return updated;
        }

        return item;
      })
      .filter((item, index) => !pathErrors[index]);
  };

  const fixRuleErrorIndexes = (ruleIndex: number, ruleErrors: boolean[]) =>
    (errorsRaw.value || [])
      .map((item) => {
        const errorItem = item as unknown[];
        if (Number(errorItem[offset.value + 1]) > ruleIndex + 1) {
          const updated = errorItem.slice(0);
          updated[offset.value + 1] = Number(updated[offset.value + 1]) - 1;
          return updated;
        }

        return item;
      })
      .filter((item, index) => !ruleErrors[index]);

  const fixPathSubEntitiesErrorIndexes = (
    ruleIndex: number,
    pathIndex: number,
    subEntityIndex: number,
    subEntityErrors: boolean[],
  ) => {
    const pathErrors = processPathError(ruleIndex, pathIndex);

    return (errorsRaw.value || [])
      .map((item, index) => {
        if (!pathErrors[index]) {
          return item;
        }

        const errorItem = item as unknown[];
        if (Number(errorItem[offset.value + 5]) > subEntityIndex + 1) {
          const updated = errorItem.slice(0);
          updated[offset.value + 5] = Number(updated[offset.value + 5]) - 1;
          return updated;
        }

        return item;
      })
      .filter((item, index) => !subEntityErrors[index]);
  };

  const fixOfferErrorIndexes = (
    ruleIndex: number,
    pathIndex: number,
    offerIndex: number,
    offerErrors: boolean[],
  ) => fixPathSubEntitiesErrorIndexes(ruleIndex, pathIndex, offerIndex, offerErrors);

  const fixLandingErrorIndexes = (
    ruleIndex: number,
    pathIndex: number,
    landingIndex: number,
    landingErrors: boolean[],
  ) => fixPathSubEntitiesErrorIndexes(ruleIndex, pathIndex, landingIndex, landingErrors);

  return {
    getRulesErrors,
    isGeneralError,
    processRuleError,
    processPathError,
    processOfferError,
    processLandingError,
    fixPathErrorIndexes,
    fixRuleErrorIndexes,
    fixOfferErrorIndexes,
    fixLandingErrorIndexes,
  };
};
